import { Breadcrumbs, Loader } from '@mantine/core';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { ApplicationState } from '@xver/store';
import { useAppDispatch } from '@xver/hooks/useAppDispatch';
import useBreadcrumbItems from '@xver/hooks/useBreadcrumbItems';
import usePageHeading from '@xver/hooks/usePageHeading';
import { useMeQuery } from '@xver/hooks/useMeQuery';
import { setAuthUser } from '@xver/store/auth/auth.actions';

import ChangeEmailSection from './components/ChangeEmailSection';
import ChangePasswordSection from './components/ChangePasswordSection';
import ChangePhoneSection from './components/ChangePhoneSection';
import EmailVerificationSection from './components/EmailVerificationSection';
import TwoFactorSection from './components/TwoFactorSection';

export default function SecurityPage() {
    const { t } = useTranslation('security');
    const headingRef = usePageHeading(t('page.title'));
    const dispatch = useAppDispatch();
    const cachedUser = useSelector((state: ApplicationState) => state.auth.user);
    const { data: freshUser, isPending } = useMeQuery();

    useEffect(() => {
        if (freshUser) dispatch(setAuthUser(freshUser));
    }, [freshUser, dispatch]);

    const user = freshUser ?? cachedUser;

    const breadcrumbItems = useBreadcrumbItems([
        { title: t('breadcrumb.account'), href: '#' },
        { title: t('breadcrumb.security') },
    ]);

    return (
        <div className="w-full min-h-full flex flex-col justify-start items-center gap-8">
            <div className="w-full flex flex-col justify-start items-start">
                <Breadcrumbs separatorMargin="xs" className="mb-6">
                    {breadcrumbItems}
                </Breadcrumbs>
                <div className="w-full flex flex-col gap-1">
                    <h1 ref={headingRef} tabIndex={-1} className="text-3xl font-bold">
                        {t('page.title')}
                    </h1>
                    <p className="w-auto max-w-135 text-base text-fg/68">{t('page.description')}</p>
                </div>
            </div>
            <div className="w-full flex flex-col gap-4">
                {!user || (isPending && !cachedUser) ? (
                    <div className="w-full flex items-center justify-center py-16">
                        <Loader size="md" type="bars" />
                    </div>
                ) : (
                    <>
                        <TwoFactorSection user={user} />
                        <EmailVerificationSection user={user} />
                        <ChangePasswordSection user={user} />
                        <ChangeEmailSection user={user} />
                        <ChangePhoneSection user={user} />
                    </>
                )}
            </div>
        </div>
    );
}
